import { createApi } from '@reduxjs/toolkit/query/react';
import { type Question } from '@strata/react-question-tree';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';

import { CLINICAL_POLICY_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PolicySummary {
    draftId: string;
    policyId: string;
    productOid: string;
    productName: string;
    diagnosesOids: string[];
    effectiveDate: string;
    statusDate: string;
    policyStatus: (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses];
    username: string;
    assignedTo?: string;
    diagnoses?: string;
}

export interface PolicyDetails {
    draftId: string;
    policyId: string;
    statusHistory: {
        policy_status: (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses];
        status_date: string;
        username: string;
        comment?: string;
        assigned_to?: string;
    }[];
    changeRequests: {
        request_date: string;
        username: string;
        change: string;
        attachments: string[];
    }[];
    questions: Question[];
}

export const clinicalPolicyApi = createApi({
    baseQuery: getBaseQuery(CLINICAL_POLICY_API_URL),
    refetchOnMountOrArgChange: true,
    reducerPath: 'clinicalPolicyApi',
    endpoints: (build) => ({
        getPolicyList: build.query<PolicySummary[], { payerId: string }>({
            query: ({ payerId }) => `${payerId}/drafts`,
            transformResponse: (
                response: {
                    policy_id: string;
                    draft_id: string;
                    product_oid: string;
                    product_name: string;
                    diagnosis_oids: string[];
                    policy_status: (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses];
                    effective_date: string;
                    status_date: string;
                    username: string;
                    assigned_to?: string;
                }[],
            ) =>
                response
                    ?.filter((policy) =>
                        (
                            [
                                ClinicalPolicyStatusIndicator.Statuses.IN_PROD,
                                ClinicalPolicyStatusIndicator.Statuses.PEND_PROD,
                                ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE,
                                ClinicalPolicyStatusIndicator.Statuses.EXTCHANGE,
                                ClinicalPolicyStatusIndicator.Statuses.EXTREVIEW,
                            ] as (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses][]
                        ).includes(policy.policy_status),
                    )
                    .map((policy) => ({
                        draftId: policy.draft_id,
                        policyId: policy.policy_id,
                        productOid: policy.product_oid,
                        productName: policy.product_name,
                        diagnosesOids: policy.diagnosis_oids,
                        effectiveDate: policy.effective_date,
                        statusDate: policy.status_date,
                        policyStatus: policy.policy_status,
                        username: policy.username?.toLowerCase(),
                        assignedTo: policy.assigned_to,
                    })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving clinical policies.',
            ),
        }),
        getPolicyDetails: build.query<
            PolicyDetails | undefined,
            { payerId: string; draftId: string }
        >({
            query: ({ payerId, draftId }) => `${payerId}/drafts/${draftId}`,
            transformResponse: (
                response: {
                    draft_id: string;
                    policy_id: string;
                    status_history: {
                        policy_status: (typeof ClinicalPolicyStatusIndicator.Statuses)[keyof typeof ClinicalPolicyStatusIndicator.Statuses];
                        status_date: string;
                        username: string;
                        comment?: string;
                        assigned_to?: string;
                    }[];
                    change_requests: {
                        request_date: string;
                        username: string;
                        change: string;
                        attachments: string[];
                    }[];
                    graph: string;
                } | null,
            ) =>
                response
                    ? {
                          draftId: response.draft_id,
                          policyId: response.policy_id,
                          statusHistory: response.status_history,
                          changeRequests:
                              // Filter out internal change requests by checking username for FMH domain
                              response.change_requests?.filter(
                                  (request) =>
                                      !/@freemarkethealth.com$/.exec(request.username ?? ''),
                              ) ?? [],
                          questions:
                              (JSON.parse(response.graph ?? '{}') as { nodes: Question[] }).nodes ??
                              [],
                      }
                    : undefined,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving clinical policy details.',
            ),
        }),
    }),
});

export const { useGetPolicyListQuery, useGetPolicyDetailsQuery } = clinicalPolicyApi;
